import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
    TextField,
    Box,
    Button,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    TablePagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
    fetchPackCodeReport,
    setPackCodeFilters,
    clearPackCodeReport,
} from '../../../store/actions/packCodeReportActions';

const QUERY_BY_OPTIONS = [
    { value: 'gigyaId', label: 'Gigya ID', placeholder: 'Enter Gigya ID' },
    { value: 'packCode', label: 'Pack Code', placeholder: 'Enter Pack Code' },
];

const PackCodeReport = () => {
    const dispatch = useDispatch();
    const { auth, customerReport } = useSelector((state) => state);
    const { market, category, brand } = auth.user;
    const {
        packCodeFilters,
        packCodeReport,
        packCodeTotalCount,
        packCodeHasSearched,
    } = customerReport;

    const [formError, setFormError] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleFilterChange = (field, value) => {
        dispatch(
            setPackCodeFilters({
                [field]: value,
            })
        );
    };

    const handleClear = () => {
        dispatch(clearPackCodeReport());
        setFormError('');
        setPage(0);
    };

    const getApiParams = () => {
        let gigyaId = '';
        let packCode = '';
        let eventType = packCodeFilters.eventType || '';

        if (packCodeFilters.queryByValue) {
            if (packCodeFilters.queryByField === 'gigyaId') {
                gigyaId = packCodeFilters.queryByValue;
            } else if (packCodeFilters.queryByField === 'packCode') {
                packCode = packCodeFilters.queryByValue;
            }
        }

        return { gigyaId, packCode, eventType };
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);

        const start = newPage * rowsPerPage;
        const { gigyaId, packCode, eventType } = getApiParams();

        dispatch(
            fetchPackCodeReport(
                market,
                category,
                brand,
                dayjs(packCodeFilters.startDate).startOf('day').toISOString(),
                dayjs(packCodeFilters.endDate).endOf('day').toISOString(),
                gigyaId,
                packCode,
                eventType,
                start,
                rowsPerPage
            )
        );
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);

        const { gigyaId, packCode, eventType } = getApiParams();

        dispatch(
            fetchPackCodeReport(
                market,
                category,
                brand,
                dayjs(packCodeFilters.startDate).startOf('day').toISOString(),
                dayjs(packCodeFilters.endDate).endOf('day').toISOString(),
                gigyaId,
                packCode,
                eventType,
                0,
                newRowsPerPage
            )
        );
    };

    const handleSubmit = () => {
        setFormError('');

        if (!packCodeFilters.startDate || !packCodeFilters.endDate) {
            setFormError('Please select both start and end dates.');
            return;
        }
        if (packCodeFilters.startDate > packCodeFilters.endDate) {
            setFormError('Start date cannot be after end date.');
            return;
        }

        setPage(0);

        const { gigyaId, packCode, eventType } = getApiParams();

        dispatch(
            fetchPackCodeReport(
                market,
                category,
                brand,
                dayjs(packCodeFilters.startDate).startOf('day').toISOString(),
                dayjs(packCodeFilters.endDate).endOf('day').toISOString(),
                gigyaId,
                packCode,
                eventType,
                0,
                rowsPerPage
            )
        );
    };

    const getPlaceholder = () => {
        const option = QUERY_BY_OPTIONS.find(
            (opt) => opt.value === packCodeFilters.queryByField
        );
        return option ? option.placeholder : 'Enter Value';
    };

    const getLabel = () => {
        const option = QUERY_BY_OPTIONS.find(
            (opt) => opt.value === packCodeFilters.queryByField
        );
        return option ? option.label : 'Value';
    };

    return (
        <div className="customer-report-child">
            <h3 className="customer-report-child-title">
                Search Transaction Criteria
            </h3>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    mb: 3,
                    maxWidth: '600px',
                }}
            >
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <DatePicker
                        label={<Box color={'#000'}>From*</Box>}
                        value={packCodeFilters.startDate}
                        onChange={(date) =>
                            handleFilterChange('startDate', date)
                        }
                        slotProps={{
                            textField: {
                                size: 'small',
                                sx: { flex: 1 },
                            },
                        }}
                    />
                    <DatePicker
                        label={<Box color={'#000'}>To*</Box>}
                        value={packCodeFilters.endDate}
                        onChange={(date) => handleFilterChange('endDate', date)}
                        minDate={packCodeFilters.startDate}
                        slotProps={{
                            textField: {
                                size: 'small',
                                sx: { flex: 1 },
                            },
                        }}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                        <InputLabel id="pack-code-query-by-field-label">
                            Query By
                        </InputLabel>
                        <Select
                            labelId="pack-code-query-by-field-label"
                            value={packCodeFilters.queryByField}
                            label="Query By"
                            onChange={(e) =>
                                handleFilterChange(
                                    'queryByField',
                                    e.target.value
                                )
                            }
                        >
                            {QUERY_BY_OPTIONS.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label={getLabel()}
                        value={packCodeFilters.queryByValue}
                        onChange={(e) =>
                            handleFilterChange('queryByValue', e.target.value)
                        }
                        placeholder={getPlaceholder()}
                        size="small"
                        sx={{ flex: 1 }}
                        inputProps={{ maxLength: 50 }}
                    />
                </Box>

                <Box>
                    <FormControl size="small" fullWidth>
                        <InputLabel id="pack-code-event-type-label">Event Type</InputLabel>
                        <Select
                            labelId="pack-code-event-type-label"
                            value={packCodeFilters.eventType}
                            label="Event Type"
                            onChange={(e) => handleFilterChange('eventType', e.target.value)}
                        >
                            <MenuItem value="">
                                <em>All</em>
                            </MenuItem>
                            <MenuItem value="UPC">UPC</MenuItem>
                            <MenuItem value="GOLDEN_TICKET">Golden Ticket</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {formError && (
                    <p className="customer-report-child-error">{formError}</p>
                )}

                <Box
                    sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleClear}
                        sx={{ minWidth: '120px' }}
                    >
                        Reset query
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={
                            !packCodeFilters.startDate ||
                            !packCodeFilters.endDate
                        }
                        sx={{ minWidth: '120px' }}
                    >
                        Search
                    </Button>
                </Box>
            </Box>

            {!packCodeHasSearched && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '300px',
                        gap: 2,
                    }}
                >
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            backgroundColor: '#E8F5E9',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <SearchIcon sx={{ fontSize: 40, color: '#4CAF50' }} />
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{ color: '#666', fontWeight: 500 }}
                    >
                        Search for Transaction Reports
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: '#999', textAlign: 'center' }}
                    >
                        Use the filters above to search for UPC and Golden
                        Ticket entry logs
                        <br />
                        and click "Search" to get started
                    </Typography>
                </Box>
            )}

            {packCodeHasSearched && packCodeReport.length > 0 && (
                <TableContainer component={Paper} sx={{ mt: 3 }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    USER GIGYA ID
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    ID
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    PACK CODE
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    EVENT TYPE
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    BRAND
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    SKU HINGE LID CODE
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    STATUS
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    CAMPAIGN DESCRIPTION
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    INPUT TIME
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {packCodeReport.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontSize: '0.85rem',
                                        }}
                                    >
                                        {row.consumerUid || '-'}
                                    </TableCell>
                                    <TableCell>{row.id || '-'}</TableCell>
                                    <TableCell>
                                        {row.packCode || '-'}
                                    </TableCell>
                                    <TableCell>
                                        {row.eventType || '-'}
                                    </TableCell>
                                    <TableCell>{row.brand || '-'}</TableCell>
                                    <TableCell>
                                        {row.packItem || '-'}
                                    </TableCell>
                                    <TableCell>
                                        {row.promosysValue || '-'}
                                    </TableCell>
                                    <TableCell>
                                        {row.campaignDescription || '-'}
                                    </TableCell>
                                    <TableCell>
                                        {row.createdTs
                                            ? dayjs(row.createdTs).format(
                                                  'YYYY-MM-DD HH:mm:ss'
                                              )
                                            : '-'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={packCodeTotalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </TableContainer>
            )}

            {packCodeHasSearched && packCodeReport.length === 0 && (
                <Box sx={{ mt: 3, textAlign: 'center', color: '#666' }}>
                    No data found for the selected criteria.
                </Box>
            )}
        </div>
    );
};

export default PackCodeReport;
